import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { resolveExactGovernedPassagePageMatch } from "../presentation/cognitoryExactGovernedPassagePageMatch";
import { buildCanonicalPdfPageTextFromItems } from "../presentation/cognitoryPdfPageTextCanonicalization";
import {
  isGovernedPdfRenderCancellation,
  scrollGovernedPdfDocumentPaneToPage,
} from "../presentation/governedPdfCanvasRenderLifecycle";
import {
  loadGovernedPdfDocumentFromBlob,
  resolvePdfTextItemStyle,
  type PdfJsDocumentView,
  type PdfJsTextItem,
} from "../presentation/cognitoryPdfJsGovernedPageLoader";
import {
  mapCanonicalMatchRangeToTextLayerHighlights,
  resolvePdfTextLayerItemHighlightSlice,
  type PdfTextLayerHighlightSpan,
} from "../presentation/cognitoryPdfPageTextHighlightMapping";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RADIUS,
} from "../theme/mainPageVisual";
import { GovernedCitationPdfThumbnailSidebar } from "./GovernedCitationPdfThumbnailSidebar";

const NARROW_SCREEN_MAX_WIDTH_PX = 768;

type PdfViewport = {
  width: number;
  height: number;
  convertToViewportPoint: (x: number, y: number) => [number, number];
};

export type GovernedCitationPdfPageMetrics = {
  width: number;
  height: number;
  scale: number;
};

type CitedPageHighlightState =
  | { status: "matched" }
  | { status: "not_found" }
  | { status: "ambiguous"; matchCount: number };

type GovernedCitationPdfDocumentState =
  | { status: "loading" }
  | {
      status: "ready";
      pdf: PdfJsDocumentView;
      numPages: number;
      citedPageNumber: number;
      citedHighlight: CitedPageHighlightState;
      citedHighlightSpans: readonly PdfTextLayerHighlightSpan[];
    }
  | { status: "error"; message: string };

const HIGHLIGHT_MARK_STYLE: CSSProperties = {
  color: "transparent",
  backgroundColor: "rgba(255, 235, 59, 0.5)",
  borderRadius: 2,
  boxDecorationBreak: "clone",
  WebkitBoxDecorationBreak: "clone",
  padding: 0,
  margin: 0,
};

function isValidGovernedPageNumber(pageNumber: number): boolean {
  return Number.isInteger(pageNumber) && pageNumber > 0;
}

function resolveCanvasOutputScale(): number {
  if (typeof window === "undefined") {
    return 1;
  }
  return window.devicePixelRatio > 1 ? window.devicePixelRatio : 1;
}

type GovernedCitationPdfPageSliceProps = {
  pdf: PdfJsDocumentView;
  pageNumber: number;
  pageScale: number;
  citedHighlightSpans: readonly PdfTextLayerHighlightSpan[];
  isCitedPage: boolean;
  onPageMetrics?: (metrics: GovernedCitationPdfPageMetrics) => void;
  onPageElement?: (pageNumber: number, element: HTMLDivElement | null) => void;
  onPageReady?: (pageNumber: number) => void;
};

const GovernedCitationPdfPageSlice = memo(
  function GovernedCitationPdfPageSlice({
    pdf,
    pageNumber,
    pageScale,
    citedHighlightSpans,
    isCitedPage,
    onPageMetrics,
    onPageElement,
    onPageReady,
  }: GovernedCitationPdfPageSliceProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const onPageMetricsRef = useRef(onPageMetrics);
    const onPageReadyRef = useRef(onPageReady);
    onPageMetricsRef.current = onPageMetrics;
    onPageReadyRef.current = onPageReady;
    const [sliceState, setSliceState] = useState<
      | { status: "loading" }
      | {
          status: "ready";
          viewport: PdfViewport;
          textItems: readonly PdfJsTextItem[];
        }
      | { status: "error"; message: string }
    >({ status: "loading" });

    useEffect(() => {
      let cancelled = false;
      let activeRenderTask: { cancel: () => void } | null = null;

      void (async () => {
        try {
          const page = await pdf.getPage(pageNumber);
          if (cancelled) {
            return;
          }

          const viewport = page.getViewport({ scale: pageScale });
          const textContent = await page.getTextContent();
          if (cancelled) {
            return;
          }

          const textItems = textContent.items.filter(
            (item): item is PdfJsTextItem =>
              typeof item.str === "string" && item.str.length > 0,
          );

          const canvas = canvasRef.current;
          if (!canvas) {
            return;
          }

          const outputScale = resolveCanvasOutputScale();
          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;

          const context = canvas.getContext("2d");
          if (!context) {
            throw new Error("Unable to acquire PDF canvas context.");
          }

          context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
          const renderTask = page.render({
            canvasContext: context,
            viewport,
          });
          activeRenderTask = renderTask;
          await renderTask.promise;

          if (cancelled) {
            return;
          }

          if (isCitedPage) {
            onPageMetricsRef.current?.({
              width: viewport.width,
              height: viewport.height,
              scale: pageScale,
            });
          }

          setSliceState({
            status: "ready",
            viewport,
            textItems,
          });
          onPageReadyRef.current?.(pageNumber);
        } catch (error) {
          if (cancelled || isGovernedPdfRenderCancellation(error)) {
            return;
          }
          setSliceState({
            status: "error",
            message:
              error instanceof Error
                ? error.message
                : "Unable to render governed PDF page.",
          });
        }
      })();

      return () => {
        cancelled = true;
        activeRenderTask?.cancel();
      };
    }, [isCitedPage, pageNumber, pageScale, pdf]);

    const highlightSpanByItem = new Map(
      citedHighlightSpans.map(span => [span.itemIndex, span]),
    );

    return (
      <div
        ref={node => {
          onPageElement?.(pageNumber, node);
        }}
        data-testid={
          isCitedPage
            ? "cognitory-citation-pdf-highlight-page-canvas"
            : `cognitory-governed-pdf-page-${pageNumber}`
        }
        data-page-number={pageNumber}
        data-cited-page={isCitedPage ? "true" : "false"}
        style={{
          position: "relative",
          width:
            sliceState.status === "ready"
              ? `${sliceState.viewport.width}px`
              : "min(860px, 100%)",
          minHeight:
            sliceState.status === "ready"
              ? `${sliceState.viewport.height}px`
              : "480px",
          backgroundColor: "#ffffff",
          boxShadow:
            "0 1px 2px rgba(15, 23, 42, 0.06), 0 12px 32px rgba(15, 23, 42, 0.12)",
          marginBottom: "1.25rem",
        }}
      >
        <canvas
          ref={canvasRef}
          data-testid={`cognitory-governed-pdf-page-canvas-${pageNumber}`}
          style={{ display: "block" }}
        />

        {sliceState.status === "ready" && isCitedPage ? (
          <div
            data-testid="cognitory-citation-pdf-highlight-text-layer"
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            {sliceState.textItems.map((item, itemIndex) => {
              const span = highlightSpanByItem.get(itemIndex);
              if (!span) {
                return null;
              }
              const slice = resolvePdfTextLayerItemHighlightSlice({
                itemText: item.str,
                span,
              });
              if (slice.highlighted.length === 0) {
                return null;
              }
              const style = resolvePdfTextItemStyle({
                item,
                viewport: sliceState.viewport,
              });

              return (
                <mark
                  key={`pdf-highlight-item-${pageNumber}-${itemIndex}`}
                  data-testid={`cognitory-citation-pdf-highlight-text-item-${itemIndex}`}
                  data-highlighted="true"
                  style={{
                    ...HIGHLIGHT_MARK_STYLE,
                    position: "absolute",
                    left: `${style.left}px`,
                    top: `${style.top}px`,
                    fontSize: `${style.fontSize}px`,
                    lineHeight: 1,
                    whiteSpace: "pre",
                    transformOrigin: "0 0",
                  }}
                >
                  {slice.highlighted}
                </mark>
              );
            })}
          </div>
        ) : null}

        {sliceState.status === "loading" ? (
          <div
            data-testid={`cognitory-governed-pdf-page-loading-${pageNumber}`}
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: MAIN_PAGE_COLORS.muted,
              fontFamily: MAIN_PAGE_FONT_STACK,
              fontSize: "0.9rem",
            }}
          >
            Loading page {pageNumber}…
          </div>
        ) : null}

        {sliceState.status === "error" ? (
          <div
            data-testid={`cognitory-governed-pdf-page-error-${pageNumber}`}
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: MAIN_PAGE_COLORS.alertInk,
              padding: "1rem",
              textAlign: "center",
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            {sliceState.message}
          </div>
        ) : null}
      </div>
    );
  },
);

export function GovernedCitationPdfPageRenderer(input: {
  blob: Blob;
  pageNumber: number;
  citedPassage: string;
  pageScale?: number;
  onPageMetrics?: (metrics: GovernedCitationPdfPageMetrics) => void;
}): JSX.Element {
  const pageScale = input.pageScale ?? 1;
  const pdfDocumentRef = useRef<PdfJsDocumentView | null>(null);
  const pageElementsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const documentScrollRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToCitedPageRef = useRef(false);
  const governedPageNumberRef = useRef(input.pageNumber);
  governedPageNumberRef.current = input.pageNumber;
  const onPageMetricsRef = useRef(input.onPageMetrics);
  onPageMetricsRef.current = input.onPageMetrics;
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [thumbnailDrawerOpen, setThumbnailDrawerOpen] = useState(false);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(input.pageNumber);
  const [documentState, setDocumentState] =
    useState<GovernedCitationPdfDocumentState>({
      status: "loading",
    });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia(
      `(max-width: ${NARROW_SCREEN_MAX_WIDTH_PX}px)`,
    );
    const update = () => {
      const narrow = mediaQuery.matches;
      setIsNarrowScreen(narrow);
      if (narrow) {
        setSidebarExpanded(false);
      }
    };
    update();
    mediaQuery.addEventListener("change", update);
    return () => {
      mediaQuery.removeEventListener("change", update);
    };
  }, []);

  const attemptInitialCitedPageScroll = useCallback(() => {
    if (hasScrolledToCitedPageRef.current) {
      return;
    }
    const scrollRoot = documentScrollRef.current;
    const pageElement = pageElementsRef.current.get(
      governedPageNumberRef.current,
    );
    if (!scrollRoot || !pageElement) {
      return;
    }
    scrollGovernedPdfDocumentPaneToPage({
      scrollRoot,
      pageElement,
      behavior: "auto",
    });
    hasScrolledToCitedPageRef.current = true;
  }, []);

  const registerPageElement = useCallback(
    (pageNumber: number, element: HTMLDivElement | null) => {
      if (element) {
        pageElementsRef.current.set(pageNumber, element);
        if (pageNumber === governedPageNumberRef.current) {
          window.requestAnimationFrame(() => {
            attemptInitialCitedPageScroll();
          });
        }
        return;
      }
      pageElementsRef.current.delete(pageNumber);
    },
    [attemptInitialCitedPageScroll],
  );

  const handlePageReady = useCallback(
    (pageNumber: number) => {
      if (pageNumber !== governedPageNumberRef.current) {
        return;
      }
      window.requestAnimationFrame(() => {
        attemptInitialCitedPageScroll();
      });
    },
    [attemptInitialCitedPageScroll],
  );

  const scrollToPage = useCallback(
    (pageNumber: number) => {
      const scrollRoot = documentScrollRef.current;
      const pageElement = pageElementsRef.current.get(pageNumber);
      if (scrollRoot && pageElement) {
        scrollGovernedPdfDocumentPaneToPage({
          scrollRoot,
          pageElement,
          behavior: "smooth",
        });
      }
      if (isNarrowScreen) {
        setThumbnailDrawerOpen(false);
      }
    },
    [isNarrowScreen],
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (!isValidGovernedPageNumber(input.pageNumber)) {
          throw new Error(
            `Governed page number is invalid: ${String(input.pageNumber)}`,
          );
        }

        const pdf = await loadGovernedPdfDocumentFromBlob(input.blob);
        pdfDocumentRef.current = pdf;
        if (cancelled) {
          pdf.destroy?.();
          pdfDocumentRef.current = null;
          return;
        }

        if (input.pageNumber > pdf.numPages) {
          throw new Error(
            `Governed page ${input.pageNumber} is out of range for this PDF (${pdf.numPages} pages).`,
          );
        }

        const citedPage = await pdf.getPage(input.pageNumber);
        const citedTextContent = await citedPage.getTextContent();
        const citedTextItems = citedTextContent.items.filter(
          (item): item is PdfJsTextItem =>
            typeof item.str === "string" && item.str.length > 0,
        );
        const canonicalPage =
          buildCanonicalPdfPageTextFromItems(citedTextItems);
        const match = resolveExactGovernedPassagePageMatch({
          canonicalPageText: canonicalPage.text,
          citedPassage: input.citedPassage,
        });

        const citedHighlightSpans =
          match.kind === "matched"
            ? mapCanonicalMatchRangeToTextLayerHighlights({
                charToItem: canonicalPage.charToItem,
                start: match.start,
                end: match.end,
              }).spans
            : [];

        if (cancelled) {
          return;
        }

        setDocumentState({
          status: "ready",
          pdf,
          numPages: pdf.numPages,
          citedPageNumber: input.pageNumber,
          citedHighlight:
            match.kind === "matched"
              ? { status: "matched" }
              : match.kind === "ambiguous"
                ? { status: "ambiguous", matchCount: match.matchCount }
                : { status: "not_found" },
          citedHighlightSpans,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setDocumentState({
          status: "error",
          message:
            error instanceof Error
              ? error.message
              : "Unable to render governed PDF document.",
        });
      }
    })();

    return () => {
      cancelled = true;
      pdfDocumentRef.current?.destroy?.();
      pdfDocumentRef.current = null;
      hasScrolledToCitedPageRef.current = false;
    };
  }, [input.blob, input.citedPassage, input.pageNumber]);

  useEffect(() => {
    if (documentState.status !== "ready") {
      return;
    }

    let observer: IntersectionObserver | null = null;
    const frame = window.requestAnimationFrame(() => {
      const scrollRoot = documentScrollRef.current;
      if (!scrollRoot) {
        return;
      }

      observer = new IntersectionObserver(
        entries => {
          const visible = entries
            .filter(entry => entry.isIntersecting)
            .sort(
              (left, right) => right.intersectionRatio - left.intersectionRatio,
            );
          const topEntry = visible[0];
          if (!topEntry?.target) {
            return;
          }
          const pageNumber = Number(
            (topEntry.target as HTMLElement).dataset.pageNumber,
          );
          if (Number.isInteger(pageNumber) && pageNumber > 0) {
            setCurrentPageNumber(pageNumber);
          }
        },
        {
          root: scrollRoot,
          threshold: [0.2, 0.35, 0.5, 0.65],
        },
      );

      for (const element of pageElementsRef.current.values()) {
        observer.observe(element);
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [documentState.status, pageScale]);

  const statusBanner =
    documentState.status === "ready" &&
    documentState.citedHighlight.status === "not_found" ? (
      <p
        data-testid="cognitory-citation-pdf-highlight-not-found"
        style={{
          margin: "0 0 1rem",
          padding: "0.65rem 0.85rem",
          borderRadius: MAIN_PAGE_RADIUS.alert,
          border: `1px solid ${MAIN_PAGE_COLORS.warnBorder}`,
          background: MAIN_PAGE_COLORS.warnFill,
          color: MAIN_PAGE_COLORS.warnInk,
          fontSize: "0.9rem",
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        Exact cited passage not found on page {documentState.citedPageNumber}
      </p>
    ) : documentState.status === "ready" &&
      documentState.citedHighlight.status === "ambiguous" ? (
      <p
        data-testid="cognitory-citation-pdf-highlight-ambiguous"
        style={{
          margin: "0 0 1rem",
          padding: "0.65rem 0.85rem",
          borderRadius: MAIN_PAGE_RADIUS.alert,
          border: `1px solid ${MAIN_PAGE_COLORS.alertBorder}`,
          background: MAIN_PAGE_COLORS.alertFill,
          color: MAIN_PAGE_COLORS.alertInk,
          fontSize: "0.9rem",
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        Exact cited passage is ambiguous on page {documentState.citedPageNumber}{" "}
        ({documentState.citedHighlight.matchCount} matches). Highlight withheld.
      </p>
    ) : documentState.status === "ready" &&
      documentState.citedHighlight.status === "matched" ? (
      <p
        data-testid="cognitory-citation-pdf-highlight-matched"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        Citation passage highlighted on page {documentState.citedPageNumber}
      </p>
    ) : null;

  if (documentState.status === "error") {
    return (
      <div data-testid="cognitory-governed-document-viewer">
        <p
          data-testid="cognitory-citation-pdf-highlight-error"
          style={{
            margin: 0,
            padding: "0.85rem 1rem",
            borderRadius: MAIN_PAGE_RADIUS.alert,
            border: `1px solid ${MAIN_PAGE_COLORS.alertBorder}`,
            background: MAIN_PAGE_COLORS.alertFill,
            color: MAIN_PAGE_COLORS.alertInk,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          {documentState.message}
        </p>
      </div>
    );
  }

  const pdf = documentState.status === "ready" ? documentState.pdf : null;

  return (
    <div
      data-testid="cognitory-governed-document-viewer"
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {statusBanner}

      <div
        data-testid="cognitory-governed-document-layout"
        style={{
          display: "flex",
          alignItems: "stretch",
          flex: 1,
          minHeight: 0,
          borderRadius: MAIN_PAGE_RADIUS.panel,
          border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
          background: MAIN_PAGE_COLORS.pageBackground,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {!isNarrowScreen &&
        sidebarExpanded &&
        documentState.status === "ready" ? (
          <GovernedCitationPdfThumbnailSidebar
            pdf={documentState.pdf}
            numPages={documentState.numPages}
            citedPageNumber={documentState.citedPageNumber}
            currentPageNumber={currentPageNumber}
            expanded={sidebarExpanded}
            drawerOpen={false}
            useDrawer={false}
            onToggleExpanded={() => setSidebarExpanded(false)}
            onCloseDrawer={() => setThumbnailDrawerOpen(false)}
            onSelectPage={scrollToPage}
          />
        ) : null}

        {isNarrowScreen && documentState.status === "ready" ? (
          <GovernedCitationPdfThumbnailSidebar
            pdf={documentState.pdf}
            numPages={documentState.numPages}
            citedPageNumber={documentState.citedPageNumber}
            currentPageNumber={currentPageNumber}
            expanded={false}
            drawerOpen={thumbnailDrawerOpen}
            useDrawer
            onToggleExpanded={() => setThumbnailDrawerOpen(true)}
            onCloseDrawer={() => setThumbnailDrawerOpen(false)}
            onSelectPage={scrollToPage}
          />
        ) : null}

        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.45rem 0.65rem",
              borderBottom: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
              background: "rgba(255, 255, 255, 0.72)",
            }}
          >
            <button
              type="button"
              data-testid="cognitory-governed-pdf-thumbnail-toggle"
              onClick={() => {
                if (isNarrowScreen) {
                  setThumbnailDrawerOpen(true);
                  return;
                }
                setSidebarExpanded(current => !current);
              }}
              style={{
                ...MAIN_PAGE_QUIET_BUTTON_STYLE,
                marginTop: 0,
                padding: "0.35rem 0.7rem",
                fontSize: "0.82rem",
              }}
            >
              {isNarrowScreen || !sidebarExpanded ? "Pages" : "Hide pages"}
            </button>
            <span
              data-testid="cognitory-governed-pdf-current-page"
              style={{
                fontSize: "0.82rem",
                color: MAIN_PAGE_COLORS.soft,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
            >
              Viewing page {currentPageNumber}
              {documentState.status === "ready"
                ? ` of ${documentState.numPages}`
                : ""}
            </span>
          </div>

          <div
            ref={documentScrollRef}
            data-testid="cognitory-governed-document-scroll"
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "clamp(0.75rem, 2vw, 1.5rem)",
              flex: 1,
              minHeight: 0,
            }}
          >
            {documentState.status === "loading" || !pdf ? (
              <div
                data-testid="cognitory-citation-pdf-highlight-loading"
                style={{
                  minHeight: "480px",
                  display: "grid",
                  placeItems: "center",
                  color: MAIN_PAGE_COLORS.muted,
                  fontFamily: MAIN_PAGE_FONT_STACK,
                }}
              >
                Loading governed PDF document…
              </div>
            ) : (
              Array.from({ length: documentState.numPages }, (_, index) => {
                const pageNumber = index + 1;
                const isCitedPage =
                  pageNumber === documentState.citedPageNumber;
                return (
                  <GovernedCitationPdfPageSlice
                    key={`governed-pdf-page-${pageNumber}`}
                    pdf={pdf}
                    pageNumber={pageNumber}
                    pageScale={pageScale}
                    citedHighlightSpans={
                      isCitedPage ? documentState.citedHighlightSpans : []
                    }
                    isCitedPage={isCitedPage}
                    onPageElement={registerPageElement}
                    onPageReady={handlePageReady}
                    onPageMetrics={metrics =>
                      onPageMetricsRef.current?.(metrics)
                    }
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
